import { Frame, Note, useWidgetText } from "../shared";
import { compareSentence, incomplete, incompleteText, localDate, periodName } from "../statistics";
import type { WidgetProps } from "../types";
import { StatisticsWidget } from "./StatisticsParts";

/**
 * period_compare: this week, month or year so far against the same days last year, as two bars and in words, with
 * last year's whole period under it (pro-statistics PST-20 to PST-24). This year's bar is the chart's first colour,
 * last year's its light step; more and less are never red and green.
 */
export function PeriodCompare(props: WidgetProps) {
  const fmt = useWidgetText();
  const { widget, tz } = props;
  const period = typeof widget.config?.period === "string" ? widget.config.period : "month";
  return (
    <StatisticsWidget
      props={props}
      body={({ answer, point, name, icon, footer, offline }) => {
        const per = answer.periods?.find((p) => p.period === period) ?? answer.periods?.[0];
        if (!per || per.value === null) return null;
        const ly = per.last_year;
        const heading = fmt.t(`statistics.${per.period}`);
        const now = fmt.value(per.value, point);
        const sentence = compareSentence(per, point, fmt.locale, fmt.t, tz);
        const thisYear = localDate(Date.parse(per.from), tz).year;
        const bars = [
          { year: thisYear, value: per.value, tone: "bg-brand" },
          ...(ly ? [{ year: localDate(Date.parse(ly.from), tz).year, value: ly.value, tone: "bg-brand/35" }] : []),
        ];
        const max = Math.max(...bars.map((b) => Math.abs(b.value)), Number.EPSILON);
        const gap = incomplete(per.coverage)
          ? incompleteText(per.period, per.coverage, fmt.t)
          : ly && incomplete(ly.coverage)
            ? incompleteText(per.period, ly.coverage, fmt.t)
            : undefined;
        const whole =
          ly && ly.full_period_value !== null
            ? fmt.t(per.aggregate === "avg" ? "statistics.onAverage" : "statistics.inAll", {
                period: periodName(per.period, localDate(Date.parse(ly.from), tz), fmt.t),
                value: fmt.value(ly.full_period_value, point),
              })
            : undefined;
        return (
          <Frame
            widget={widget}
            state={offline ? "offline" : "normal"}
            title={name}
            icon={icon}
            label={`${name}: ${heading} ${now}. ${sentence}`}
          >
            <p className="flex items-baseline gap-2">
              <span className="text-xs text-gray-700">{heading}</span>
              <span className="text-xl font-semibold tracking-tight text-gray-900 tabular-nums">{now}</span>
            </p>
            {ly && (
              <ul aria-hidden="true" className="flex flex-col gap-1">
                {bars.map((b) => (
                  <li key={b.year} className="grid grid-cols-[3rem_1fr_auto] items-center gap-2 text-xs">
                    <span className="text-gray-700 tabular-nums">{b.year}</span>
                    <span className="h-2.5 rounded-sm bg-gray-100">
                      <span
                        className={`block h-full rounded-sm ${b.tone}`}
                        style={{ width: `${((Math.abs(b.value) / max) * 100).toFixed(1)}%` }}
                      />
                    </span>
                    <span className="text-gray-900 tabular-nums">{fmt.value(b.value, point)}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-gray-900">{sentence}</p>
            {gap && <Note tone="warn">{gap}</Note>}
            {whole && <Note>{whole}</Note>}
            {footer}
          </Frame>
        );
      }}
    />
  );
}
